import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { combineLatest, distinctUntilChanged, map, Observable, startWith, Subscription } from 'rxjs';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { Shopper, ShopperService } from '../..';
import { MatSidenav } from '@angular/material/sidenav';
import { Filter } from 'src/app/@shared/models/filter.model';
import { MatOptionSelectionChange } from '@angular/material/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

const log = new Logger('ShoppersComponent');

@Component({
  selector: 'app-shoppers',
  templateUrl: './shoppers.component.html',
  styleUrls: ['./shoppers.component.scss']
})

export class ShoppersComponent<T extends Shopper> implements OnInit {

 @ViewChild(MatSidenav) sidenav!: MatSidenav;

 
  viewModel$ = combineLatest([
    this.shopperService.shoppers$,
    this.shopperService.isLoading$,
    this.shopperService.totalRecords$,
    this.shopperService.page$,
    this.shopperService.cities$,
    this.shopperService.states$,
    this.shopperService.filters$
  ]).pipe(
    map(([shoppers, isLoading, totalRecords, page, cities, states, filters]) => {
      if (shoppers.length == 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0
        page.pageIndex = 0
        this.shopperService.page(page);
        this.shopperService.reload();
      }
      this.cityNames = cities;
      return { shoppers, isLoading, totalRecords, page, cities, states, filters }
    }),
  );

  filtersForm = new FormGroup({
    search: new FormControl<string | null>(null),
    state: new FormControl<string>(''),
    city: new FormControl<string>(''),
  });

  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'LastName', 'FirstName', 'Email', 'Phone', 'City', 'State', 'Actions'];
  cityNames: string[] = [];
  filteredCityNames$: Observable<string[]> | undefined;
  shopperList: Shopper[] = [];
  selection = new SelectionModel<Shopper>(true, []);


  constructor(private shopperService: ShopperService<T>, private mediaObserver: MediaObserver,private dialog: MatDialog,
    private matSnackBar: MatSnackBar) { }

  ngOnInit(): void {
    log.debug('init');
    this.shopperService.shoppers$.subscribe((data) => {
      this.shopperList = data;
    })
    this.clearSearch();
    this.shopperService.reload();

    this.filtersForm.controls.search.setValue(this.shopperService.search);

    this.filteredCityNames$ = this.filtersForm.controls.city.valueChanges.pipe(
      startWith(''),
      map(value => this.filterCityNames(value ? value : '')),
    );

    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'LastName', 'Actions'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select','LastName', 'FirstName', 'City', 'State', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'LastName', 'FirstName', 'Email', 'Phone', 'City', 'State', 'Actions'];
        }
      });
  }

  onSearch(event: any) {
    this.shopperService.search = event.target.value;
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.shopperService.search = '';
  }

  onFilterChange(event: any) {
    switch (event.source.ngControl.name.toLowerCase()) {
      case 'state':
        // get the selected states 
        const statesValues: string[] = event.value;
        //remove any existing states filter  
        this.shopperService.removeFilterByFieldName('states');
        // init the array of states filters
        const statesFilters: Filter[] = [];
        // loop through the selected states and add each as a filter
        statesValues.forEach(selection => {
          // init the new filter
          let state = selection ? selection : '';
          const statesFilter: Filter = { displayText: 'State', fieldName: 'states', value: state };
          // add the filter to the list of filters
          statesFilters.push(statesFilter)
        });
        // add the filters to the event service
        this.shopperService.addFilters(statesFilters);
        break;
    }
  }

  resetFilters() {
    this.filtersForm.reset();
    this.shopperService.search = '';
    this.shopperService.clearFilters();
    this.sidenav.toggle();
  }

  
  removeFilter(filter: Filter) {
    this.shopperService.removeFilter(filter);
  }

  clearFilters() {
    this.filtersForm.reset();
    this.shopperService.clearFilters();
  }

  onSort(sortState: Sort): void {
    this.shopperService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.shopperService.page(pageEvent);
  }

  onCityChange(event: MatOptionSelectionChange, city: string): void {
    // remove any existing cities id filters
    event.source.selected && this.shopperService.removeFilterByFieldName('cities');

    if (event.source.selected && city.length > 0) {
      // init the array of cities filters
      const citiesFilters: Filter[] = [];
      // init the city filter
      const citiesFilter: Filter = { displayText: 'City', fieldName: 'cities', value: city };
      // add the filter to the list of filters
      citiesFilters.push(citiesFilter)
      // add the filters to the event service
      this.shopperService.addFilters(citiesFilters);
    }
  }

  filterCityNames(value: string): string[] {
    const filterValue = value?.toLowerCase();
    return this.cityNames.filter(option => option.toLowerCase().includes(filterValue));
  }


  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.shopperList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.shopperList.forEach(row => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }

  deleteSelectedRecords() {
    let selectedRecords: any;   
    selectedRecords = this.getSelectedSectionRecords();    
    if (selectedRecords && selectedRecords.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove Shopper(s)?`,
        },
        disableClose: true,
      });

      confirmDialog.afterClosed().subscribe(
        confirmResult => {
          if (confirmResult) {
            this.shopperService.deleteShoppers(selectedRecords).subscribe({
              next: () => {
                this.matSnackBar.open(`Shopper(s) deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
                this.shopperService.reload();               
                  this.clearSelection();                
              },
              error: (error) => {
                log.error('Error in deleting Shopper', error);

                if (error.error.value) {
                  throw new Error(error.error.value);
                } else {
                  throw new Error(error.message);
                }
              }
            });
          }
        });
    }
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
 
}
